import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { EvDialogModule } from "ev-dialog";
import { BehaviorSubject, Observable, Subscription, combineLatest } from "rxjs";
import { LeftSidebarService } from "src/services/left-sidebar.service";
import { MapMode, MapStateService } from "src/services/map/map-state.service";
import { RightSidebarService } from "src/services/right-sidebar.service";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { EvTooltipSimpleComponent } from "src/app/shared-components/ev-tooltip-simple/ev-tooltip-simple.component";

@Component({
    selector: "map-mode-buttons",
    imports: [CommonModule, RouterModule, ReactiveFormsModule, EvDialogModule, EvTooltipSimpleComponent],
    templateUrl: "./map-mode-buttons.component.html",
    styleUrl: "./map-mode-buttons.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapModeButtonsComponent implements OnInit {
    @Output() openWeb40: EventEmitter<MapMode> = new EventEmitter();
    initialMapModeChosenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    initialMapModeChosen$: Observable<boolean> = this.initialMapModeChosenSubject.asObservable();
    currentMapMode: MapMode;
    roles: iMapModeRoles;
    expanded: boolean = false;
    formControl: FormControl<MapMode> = new FormControl<MapMode>(null);
    stringValues: Partial<Record<MapMode, string>> = {
        [MapMode.Market]: "Salg",
        [MapMode.Market2]: "Salg",
        [MapMode.Properties]: "Eiendom",
        [MapMode.NewDwellings]: "Nybygg",
        [MapMode.CommercialSale]: "Salg",
    };
    mapMode = MapMode;
    showMissingRoleDialog: boolean = false;
    missingRoleTitle: string = "";
    dialogHeaders: Partial<Record<MapMode, string>> = {
        [MapMode.Market]: "Marked",
        [MapMode.Market2]: "Marked",
        [MapMode.Properties]: "Eiendom",
        [MapMode.NewDwellings]: "Nybygg",
        [MapMode.CommercialSale]: "Næring",
    };
    subs: Subscription[] = [];
    rightSidebarVisible = inject(RightSidebarService).isOpen;
    leftSidebarVisible = inject(LeftSidebarService).isOpen;
    private readonly mapStateService = inject(MapStateService);

    constructor(private router: Router, private authService: EvAuthorizationService) {
        let estatesInMapPermission$ = this.authService.hasPermission(EVServiceType.EvWeb_EstatesInMap);
        let newDwellingsPermission$ = this.authService.hasPermission(EVServiceType.EvWeb_NewDwellings);
        let commercialPermission$ = this.authService.hasPermission(EVServiceType.EvWeb_Commercial);
        let debugPermission$ = this.authService.hasPermission(EVServiceType.Internal_Debug);
        combineLatest([estatesInMapPermission$, newDwellingsPermission$, commercialPermission$, debugPermission$])
            .pipe(takeUntilDestroyed())
            .subscribe(([estates, newDwellings, commercial, debugPermission]) => {
                this.roles = {
                    hasViewEstatesInViewRole: estates,
                    hasNewDwellingsRole: newDwellings,
                    hasCommercialRole: commercial,
                    hasDebugRole: debugPermission,
                };
                this.chooseInitialMapMode();
            });
    }

    ngOnInit(): void {}

    ngOnDestroy(): void {
        this.subs.forEach((x) => x.unsubscribe());
    }

    dropdownClicked(): void {
        this.expanded = !this.expanded;
    }

        chooseOption(mapMode: MapMode): void {
        if (!this.hasMapModeRole(mapMode)) {
            this.missingRoleTitle = this.dialogHeaders[mapMode];
            this.showMissingRoleDialog = true;
            return;
        }
        
        let queryParams: Record<string, string | number | boolean> = {areaId: null, areaTypeId: null, poly: null,favoritelist: null};
        
        this.currentMapMode = mapMode;
        this.mapStateService.updateMapMode(this.currentMapMode);
        this.formControl.setValue(this.currentMapMode);
        this.expanded = false;
        if (mapMode === MapMode.CommercialSale) {
            this.mapStateService.updateMapMode(MapMode.CommercialSale);
            this.router.navigate(["/map/commercial/market"], { queryParams , queryParamsHandling: 'merge' });
        } else if (mapMode === MapMode.NewDwellings) {
            this.mapStateService.updateMapMode(MapMode.NewDwellings);
            this.router.navigate(["/map/new-dwellings/market"], { queryParams, queryParamsHandling: 'merge' });
        } else if (mapMode === MapMode.Market2) {
            this.mapStateService.updateMapMode(MapMode.Market2);
            this.router.navigate(["/map/estate/market"], {  queryParamsHandling: 'merge' });
        } else {
            this.openWeb40.emit(mapMode);
        }
    }

    closeMissingRoleDialog(): void {
        this.showMissingRoleDialog = false;
    }

    private chooseInitialMapMode(): void {
        let route = this.router.url.toLowerCase().split("?");
        switch (route[0]) {
            case "/map/commercial/market":
                this.chooseOption(MapMode.CommercialSale);
                break;
            case "/map/new-dwellings/market":
                this.chooseOption(MapMode.NewDwellings);
                break;
            case "/map/estate/market":
                this.chooseOption(MapMode.Market2);
                break;
            case "/map/estate/estates":
                this.chooseOption(MapMode.Properties);
                break;
            case "/map/estate/projects":
                this.chooseOption(MapMode.Properties);
                break;
        }
        this.formControl.setValue(this.currentMapMode);
        this.initialMapModeChosenSubject.next(true);
    }

    private hasMapModeRole(mapMode: MapMode): boolean {
        switch (mapMode) {
            case MapMode.Properties:
                return this.roles.hasViewEstatesInViewRole;
            case MapMode.NewDwellings:
                return this.roles.hasNewDwellingsRole;
            case MapMode.CommercialSale:
                return this.roles.hasCommercialRole;
            case MapMode.NewDwellings:
                return this.roles.hasNewDwellingsRole;
            default:
                return true;
        }
    }
}

export interface iMapModeRoles {
    hasViewEstatesInViewRole: boolean;
    hasNewDwellingsRole: boolean;
    hasCommercialRole: boolean;
    hasDebugRole: boolean;
}
